import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Tooltip } from "@mui/material";

const GrossSalesInfo = ({ token }) => {
  const [goal, setGoal] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    if (!token) return;

    const fetchGoal = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/gross-sales`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const result = response.data;
        if (result && typeof result === "object") {
          setGoal(result);
        } else {
          setGoal({ amount: 0, currentAmt: 0, day: "Unknown", date: null });
        }
      } catch (err) {
        if (
          err.response?.status === 403 &&
          err.response?.data?.message === "Gross sales are disabled."
        ) {
          setDisabled(true);
        } else {
          console.error("Failed to fetch Gross sales goal:", err);
          setGoal({ amount: 0, currentAmt: 0, day: "Unknown", date: null });
        }
      } finally {
        setTimeout(() => setLoading(false), 100);
      }
    };

    fetchGoal();
  }, [token]);

  if (disabled) return null;

  const displayAmt = (amt = 0) =>
    `₱${amt.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const progress = goal?.amount
    ? Math.min((goal.currentAmt / goal.amount) * 100, 100)
    : 0;

  const displayProgress = `${progress.toFixed(1)}%`;
  const progressColor = progress > 50 ? "#fff" : theme.palette.text.primary;

  const formattedDate = goal?.date && goal?.day ? `${goal.day}` : "Today";

  return (
    <Box
      display="flex"
      justifyContent={{ xs: "center", sm: "flex-end" }}
      mb={2}
      position="relative"
      width="100%"
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 300 },
          minHeight: 100,
          border: "2px solid",
          borderColor: "primary.main",
          borderRadius: 2,
          padding: { xs: 2, sm: 1 },
          filter: loading ? "blur(3px)" : "none",
          pointerEvents: loading ? "none" : "auto",
          fontSize: { xs: "1rem", sm: "0.85rem" },
        }}
      >
        <CardContent sx={{ padding: "8px !important" }}>
          <Typography variant="caption" fontWeight="bold">
            {goal?.mode === "net" ? "Net Sales Goal" : "Gross Sales Goal"} for{" "}
            {formattedDate}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "0.9rem", sm: "0.75rem" },
              marginTop: "4px",
              display: "block",
              lineHeight: 1.3,
            }}
          >
            {/* {goal?.currentAmtAll != null &&
              goal.currentAmtAll !== goal.currentAmt && (
                <Tooltip title="Gross with Voided Sales">
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "red", display: "inline" }}
                  >
                    ({displayAmt(goal.currentAmtAll)}){" "}
                  </Typography>
                </Tooltip>
              )} */}
            {displayAmt(goal?.currentAmt)} / {displayAmt(goal?.amount)}
          </Typography>

          <Box position="relative" mt={1}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 12, // thinner bar
                borderRadius: 5,
                backgroundColor: "#eee",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: progress >= 100 ? "#4caf50" : "#1976d2",
                },
              }}
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ pointerEvents: "none" }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ color: progressColor }}
              >
                {displayProgress}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          <CircularProgress size={20} color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default GrossSalesInfo;
